import { z } from 'zod';

/**
 * Zod's built-in messages are English prose ("Too small: expected string to
 * have >=8 characters"). The API promises a translated `message` for every
 * failure, so each built-in issue is mapped onto an i18n key here instead.
 *
 * Schemas in this package that pass an explicit `message` — all the
 * domain-specific ones — bypass this map entirely and keep their own key,
 * which is why `validation.slug`, `validation.decimal` and friends survive
 * untouched.
 */
const CODE_TO_KEY: Record<string, string> = {
  invalid_type: 'validation.type',
  too_small: 'validation.tooSmall',
  too_big: 'validation.tooBig',
  not_multiple_of: 'validation.invalid',
  unrecognized_keys: 'validation.invalid',
  invalid_union: 'validation.invalid',
  invalid_key: 'validation.invalid',
  invalid_element: 'validation.invalid',
  invalid_value: 'validation.invalid',
  custom: 'validation.invalid',
};

interface RawIssue {
  code: string;
  message?: unknown;
  origin?: string;
  format?: string;
  input?: unknown;
}

export function installZodErrorMap(): void {
  z.config({
    customError: (issue) => {
      const raw = issue as unknown as RawIssue;

      // An explicit message authored in a schema is already an i18n key.
      if (typeof raw.message === 'string' && raw.message.includes('.')) {
        return raw.message;
      }

      switch (raw.code) {
        case 'invalid_type':
          // A missing field is a "required" error, not a type error.
          return raw.input === undefined ? 'validation.required' : 'validation.type';

        case 'too_small':
          return raw.origin === 'string' ? 'validation.tooShort' : 'validation.tooSmall';

        case 'too_big':
          return raw.origin === 'string' ? 'validation.tooLong' : 'validation.tooBig';

        case 'invalid_format':
          return raw.format === 'email' ? 'validation.email' : 'validation.format';

        default:
          return CODE_TO_KEY[raw.code] ?? 'validation.invalid';
      }
    },
  });
}
